import { createFileRoute, getRouteApi, useNavigate } from "@tanstack/react-router";
import { asSearchString } from "@repo/utils/search-params";
import { Button, Input, toast } from "@heroui/react";
import { Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { MyBlacklistUser } from "@repo/api/blacklist.type";
import { useTemplateProtectionGuidelines } from "@repo/context/template-protection-guidelines";
import { BlacklistUserListItem } from "@repo/ui/pages/template_service/blacklist/BlacklistUserListItem";
import { InfiniteScroll } from "@/components/infinite-scroll";
import {
  useAddToBlacklist,
  useBlacklistSet,
  useBlacklistSetUsersInfinite,
} from "@repo/ui/pages/template_service/blacklist/use-blacklist";

export type BlacklistPreviewSearch = {
  search_uid?: string;
  search_reason?: string;
};

export const Route = createFileRoute("/template_protections/blacklist/public/$id")({
  validateSearch: (search: Record<string, unknown>): BlacklistPreviewSearch => ({
    search_uid: asSearchString(search.search_uid),
    search_reason: asSearchString(search.search_reason),
  }),
  component: BlacklistPreviewPage,
});

const PAGE_SIZE = 20;
const previewRouteApi = getRouteApi("/template_protections/blacklist/public/$id");

export function BlacklistPreviewPage() {
  const { id } = previewRouteApi.useParams();
  const { search_uid: searchUid, search_reason: searchReason } =
    previewRouteApi.useSearch();
  const routeNavigate = previewRouteApi.useNavigate();
  const navigate = useNavigate();
  const { hasAgreedToGuidelines } = useTemplateProtectionGuidelines();

  const { data: item } = useBlacklistSet(id);

  // 输入 400ms 防抖后再进 query key，避免每敲一个字就请求
  const [debouncedFilters, setDebouncedFilters] = useState(() => ({
    uid: searchUid?.trim() ?? "",
    reason: searchReason ?? "",
  }));

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedFilters({
        uid: searchUid?.trim() ?? "",
        reason: searchReason ?? "",
      });
    }, 400);
    return () => window.clearTimeout(t);
  }, [searchUid, searchReason]);

  const {
    data,
    error,
    isPending: loading,
    hasNextPage,
    isFetchingNextPage: loadingMore,
    fetchNextPage,
  } = useBlacklistSetUsersInfinite(id, PAGE_SIZE, debouncedFilters);
  const list: MyBlacklistUser[] = data?.pages.flatMap((p) => p.list) ?? [];
  const hasMore = hasNextPage ?? false;

  // 预览加载失败（集合不存在 / 不可见）退回公共黑名单列表
  useEffect(() => {
    if (error) {
      toast.danger((error as { msg?: string }).msg || "加载预览失败");
      navigate({ to: "/template_protections/blacklist/public", replace: true });
    }
  }, [error, navigate]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    void fetchNextPage();
  }, [loading, loadingMore, hasMore, fetchNextPage]);

  const addToBlacklist = useAddToBlacklist();
  const handleAddBlackList = (uid: string, reason: string) => {
    addToBlacklist.mutate({ uid, reason });
  };

  return (
    <div className="px-4 pt-3 space-y-3">
      {item && (
        <button
          type="button"
          className="text-sm text-gray-500 text-left"
          onClick={() =>
            navigate({
              to: "/template_protections/trusted-query",
              search: { search: item.uid },
            })
          }
        >
          分享者：{item.nickname}（{item.uid}）
        </button>
      )}

      <div className="flex flex-col gap-2">
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <Input
            placeholder="搜索 UID..."
            value={searchUid ?? ""}
            onChange={(e) =>
              void routeNavigate({
                search: (prev) => ({
                  ...prev,
                  search_uid: e.target.value || undefined,
                }),
              })
            }
            className="w-full pl-8 rounded-full"
          />
        </div>
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <Input
            placeholder="搜索理由..."
            value={searchReason ?? ""}
            onChange={(e) =>
              void routeNavigate({
                search: (prev) => ({
                  ...prev,
                  search_reason: e.target.value || undefined,
                }),
              })
            }
            className="w-full pl-8 rounded-full"
          />
        </div>
      </div>

      <InfiniteScroll
        loading={loadingMore || loading}
        hasMore={hasMore}
        onLoadMore={loadMore}
        loadingText="加载中"
        noMoreText="没有更多了"
      >
        {list.length === 0 && !loading ? (
          <div className="py-10 text-center text-gray-500 text-sm">
            暂无黑名单用户
          </div>
        ) : (
          list.map((row) => (
            <BlacklistUserListItem
              key={row.uid}
              item={row}
              onPress={() =>
                navigate({
                  to: "/template_protections/trusted-query",
                  search: { search: row.uid },
                })
              }
              actions={
                <Button
                  size="sm"
                  variant="danger-soft"
                  className="rounded-full"
                  isDisabled={!hasAgreedToGuidelines}
                  onPress={() =>
                    handleAddBlackList(
                      row.uid,
                      row.reason
                        ? `来自：${item?.name} - ${row.reason}`
                        : "-",
                    )
                  }
                >
                  拉黑
                </Button>
              }
            />
          ))
        )}
      </InfiniteScroll>
    </div>
  );
}
